import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Radio,
  RadioGroup,
  Typography,
  Alert,
  FormControl,
  FormLabel,
  ButtonGroup,
  FormControlLabel
} from '@mui/material';
import { generateNumericId } from '../../utils';
import { useUpdatePropertyMutation } from '../../store/docApi';
import { useUploadImageMutation } from '../../store/mediaApi';

const imgUrl = 'https://m.stitchso.com/img';

export default function FileUpload({ folder, item }) {
  const [file, setFile] = useState();
  const [updateProperty] = useUpdatePropertyMutation();
  const [uploadImage, { isLoading: isUploading }] = useUploadImageMutation();
  const [filename, setFilename] = useState();
  const [uploadedFilename, setUploadedFilename] = useState();
  const [message, setMessage] = useState();
  const [transform, setTransform] = useState(false);
  const [size, setSize] = useState('medium');
  const [type, setType] = useState('webp');
  const [fileExt, setFileExt] = useState();

  function handleFileChange(e) {
    if (e.target.files) {
      let newfile = e.target.files[0];
      let tok = newfile.type.split('/');
      if (tok.length < 2) {
        setMessage('Invalid filename');
        return;
      }
      setFileExt(tok[tok.length - 1]);
      let newfilename = `${folder}/${item.id}/${generateNumericId(8)}`;
      setFilename(newfilename);
      setFile(newfile);
      setUploadedFilename();
    }
  }

  function clearInput() {
    setMessage();
    setFile();
    setFilename();
    setFileExt();
  }

  function handleUploadClick() {
    if (!file) {
      setMessage('Please select a file to upload');
      return;
    }
    setMessage();
    let ext = transform ? type : fileExt;
    let data = { file: file, filename: filename, ext: ext, transform: transform, size: size };
    uploadImage(data)
      .unwrap()
      .then((result) => {
        if (result && result.error) {
          setMessage('Error ' + result.error.status);
        } else {
          // Save the picture with the item
          let pictureFilename = `${imgUrl}/${filename}.${ext}`;
          setUploadedFilename(pictureFilename);
          let pictures = item.pictures ? [...item.pictures] : [];
          pictures.push(pictureFilename);
          updateProperty({ label: item.label, pk: item.pk, id: item.id, name: 'pictures', value: pictures });
          clearInput();
        }
      });
  }

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6">Add Picture</Typography>
      {message && <Alert severity="error">{message}</Alert>}
      {isUploading && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {file ? (
        <Box>
          {file.name} - {file.type}
        </Box>
      ) : (
        <input type="file" onChange={handleFileChange} accept="image/*" />
      )}
      <FormControlLabel
        control={<Checkbox checked={transform} onChange={(e) => setTransform(e.target.checked)} />}
        label="Transform image"
      />
      {transform && (
        <Box>
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Size</FormLabel>
            <RadioGroup row name="size" value={size} onChange={(e) => setSize(e.target.value)}>
              <FormControlLabel value="small" control={<Radio />} label="Small" />
              <FormControlLabel value="medium" control={<Radio />} label="Medium" />
              <FormControlLabel value="original" control={<Radio />} label="Original" />
            </RadioGroup>
          </FormControl>
          <FormControl>
            <FormLabel>Type</FormLabel>
            <RadioGroup row name="type" value={type} onChange={(e) => setType(e.target.value)}>
              <FormControlLabel value="png" control={<Radio />} label="PNG" />
              <FormControlLabel value="jpeg" control={<Radio />} label="JPEG" />
              <FormControlLabel value="webp" control={<Radio />} label="WEBP" />
            </RadioGroup>
          </FormControl>
        </Box>
      )}
      <ButtonGroup size="small" sx={{ mt: 2 }}>
        <Button onClick={handleUploadClick} disabled={!file}>
          Upload
        </Button>
        <Button variant="outlined" onClick={clearInput} disabled={!file}>
          Clear
        </Button>
      </ButtonGroup>
      {filename && fileExt && (
        <Typography>
          {filename}.{fileExt}
        </Typography>
      )}
      {uploadedFilename && (
        <Box>
          <img style={{ padding: 10, maxHeight: 600, maxWidth: 600 }} src={uploadedFilename} alt={uploadedFilename} />
          <Typography variant="body2">{uploadedFilename}</Typography>
        </Box>
      )}
    </Card>
  );
}
